import { Box, Chip } from "@mui/joy";
import { colorTokens, textTokens, withAlpha } from "styles/globalStyles";
import { MilitaryTechIcon, PersonIcon, GroupsIcon } from "styles/StyledMUI";

export type ExcursionCountChipType = "lead" | "people" | "groups" | "totalPeople";

export type ExcursionCountWithMaxChipProps = {
  type: ExcursionCountChipType;
  count: number;
  max?: number;
  /** xs = compact (card), sm = default (tooltip) */
  size?: "xs" | "sm";
  /** Optional aria-label for accessibility */
  "aria-label"?: string;
};

const ICONS = {
  lead: MilitaryTechIcon,
  people: PersonIcon,
  groups: GroupsIcon,
  totalPeople: PersonIcon,
};

function limitColor(count: number, max: number | undefined): "neutral" | "warning" | "danger" {
  if (max == null || max <= 0) return "neutral";
  if (count >= max) return "danger";
  if (count >= 0.8 * max) return "warning";
  return "neutral";
}

export function ExcursionCountWithMaxChip({
  type,
  count,
  max,
  size = "sm",
  "aria-label": ariaLabel,
}: ExcursionCountWithMaxChipProps) {
  const Icon = ICONS[type];
  const label = max != null ? `${count} / ${max}` : String(count);
  const chipSize = size === "xs" ? "sm" : "sm";
  const fontSize = size === "xs" ? "0.6rem" : "0.65rem";
  const iconFontSize = size === "xs" ? "0.875rem" : "1rem";
  const color = limitColor(count, max);
  const stateSx =
    color === "danger"
      ? {
          backgroundColor: `${withAlpha(colorTokens.status.danger, 0.5)} !important`,
          color: `${textTokens.color.primary} !important`,
          "& .MuiChip-label": { color: `${textTokens.color.primary} !important` },
        }
      : color === "warning"
        ? {
            backgroundColor: `${withAlpha(colorTokens.status.warning, 0.5)} !important`,
            color: `${textTokens.color.primary} !important`,
            "& .MuiChip-label": { color: `${textTokens.color.primary} !important` },
          }
        : undefined;

  const defaultAriaLabel =
    type === "lead"
      ? `Encadrants : ${label}`
      : type === "people"
        ? `Clients : ${label}`
        : type === "totalPeople"
          ? `Personnes : ${label}`
          : `Groupes : ${label}`;

  return (
    <Chip
      size={chipSize}
      variant="soft"
      color={color}
      aria-label={ariaLabel ?? defaultAriaLabel}
      startDecorator={<Icon sx={{ fontSize: iconFontSize, opacity: 0.9 }} />}
      sx={{
        fontSize,
        minHeight: size === "xs" ? 22 : 24,
        "& .MuiChip-label": { gap: 0.25 },
        ...stateSx,
      }}
    >
      {label}
    </Chip>
  );
}

export type ExcursionGroupLimitsChipsProps = {
  leadCount: number;
  peopleCount: number;
  maxLead?: number;
  maxPeople?: number;
  size?: "xs" | "sm";
};

export function ExcursionGroupLimitsChips({
  leadCount,
  peopleCount,
  maxLead,
  maxPeople,
  size = "sm",
}: ExcursionGroupLimitsChipsProps) {
  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "nowrap" }}>
      <ExcursionCountWithMaxChip type="lead" count={leadCount} max={maxLead} size={size} />
      <ExcursionCountWithMaxChip type="people" count={peopleCount} max={maxPeople} size={size} />
    </Box>
  );
}

export type ExcursionTemplateLimitsChipsProps = {
  totalPeople: number;
  groupCount: number;
  maxPeople?: number;
  maxGroups?: number;
  size?: "xs" | "sm";
};

/** Chips for excursion-level limits (template sortie): total people (users+customers) + number of groups. */
export function ExcursionTemplateLimitsChips({
  totalPeople,
  groupCount,
  maxPeople,
  maxGroups,
  size = "sm",
}: ExcursionTemplateLimitsChipsProps) {
  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "nowrap" }}>
      <ExcursionCountWithMaxChip
        type="totalPeople"
        count={totalPeople}
        max={maxPeople}
        size={size}
      />
      <ExcursionCountWithMaxChip type="groups" count={groupCount} max={maxGroups} size={size} />
    </Box>
  );
}
